import {
    ICreateNotificationListResponse,
    ICreateNotificationPayload,
    INotificationErrorResult,
    INotificationListResponse,
    INotificationUnreadCountResponse,
    IReadNotificationResponse,
} from "@elit-hair/elitklinik-shared";
import { NotificationRecipientExtrases, Platforms } from "@elit-hair/elitklinik-shared/constants";
import {
    createNotifications,
    getNotifications,
    getUnreadNotificationCount,
    readAllNotifications,
    readNotifications,
    subscribeToNotifications,
} from "@elit-hair/elitklinik-shared/notification";
import * as Sentry from "@sentry/react";
import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { enqueueSnackbar } from "notistack";

import { QUERY_KEYS, useGetNotificationAxiosInstance } from "@/constants";
import { bitOperation } from "@/helpers/bitwise";
import { queryClient } from "@/main";
import useAuth from "@/providers/hooks/useAuth";
import { NotificationCategories } from "@/types/global";

const handleNotificationError = (error: INotificationErrorResult) => {
    if (!error) return;
    if (error.status !== 403 && error.status !== 401) {
        Sentry.captureException(error.message);
        enqueueSnackbar(error.message, {
            variant: "error",
        });
    }

    return new Error(error.message);
};

export const useGetNotifications = ({ category }: { category?: NotificationCategories }) => {
    const { auth } = useAuth();
    const axiosInstance = useGetNotificationAxiosInstance();

    return useInfiniteQuery({
        enabled: !!auth?.accessToken,
        queryKey: [QUERY_KEYS.GET_NOTIFICATIONS, category],
        queryFn: async ({ pageParam = 1 }) => {
            const page = typeof pageParam === "number" ? pageParam : 1;

            const res = await getNotifications(axiosInstance, {
                page,
                limit: 10,
                category,
                formatNotification: false,
                formatTemplateClassName: "text-indigo-900  font-medium",
            });
            if (!res.success) throw handleNotificationError(res);
            return res.data;
        },
        getNextPageParam: (lastPage) => {
            const hasMore = lastPage.page * lastPage.limit < lastPage.total;
            return hasMore ? lastPage.page + 1 : undefined;
        },
        initialPageParam: 1,
    });
};

export const useCreateNotification = () => {
    const axiosInstance = useGetNotificationAxiosInstance();
    return useMutation<ICreateNotificationListResponse, Error, ICreateNotificationPayload>({
        mutationFn: async (data) => {
            const res = await createNotifications(axiosInstance, data);
            if (!res.success) throw handleNotificationError(res);
            return res.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.GET_NOTIFICATIONS],
            });
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.GET_UNREAD_COUNT],
            });
        },
    });
};

export const useMarkNotificationAsRead = ({ category }: { category?: NotificationCategories }) => {
    const axiosInstance = useGetNotificationAxiosInstance();

    return useMutation<IReadNotificationResponse, Error, string[]>({
        mutationFn: async (ids) => {
            const res = await readNotifications(axiosInstance, {
                notificationIds: ids,
            });

            if (!res.success) {
                Sentry.captureException(res.message);
                enqueueSnackbar(res.message, {
                    variant: "error",
                });
                throw new Error(res.message);
            }

            return res.data;
        },
        onSuccess: (responses) => {
            const completedList = new Set<string>();

            for (const [index, response] of responses.entries()) {
                if (response.ok) {
                    completedList.add(response.uniqueIdentifier);
                }
                setTimeout(() => {
                    if (!response.ok) {
                        Sentry.captureException(new Error(response.message));
                        enqueueSnackbar(response.message || "", {
                            variant: "error",
                        });
                        return;
                    }
                }, index * 50);
            }

            if (completedList.size) {
                queryClient.setQueryData([QUERY_KEYS.GET_NOTIFICATIONS, category], (old: unknown) => {
                    if (!old) return old;

                    const typedOld = old as { pages: INotificationListResponse[] };
                    if (!typedOld || !typedOld.pages) return old;

                    for (const page of typedOld.pages) {
                        for (const notification of page.list) {
                            if (completedList.has(notification.recipient.id)) {
                                notification.recipient.readAt = DateTime.now().toISO();
                                notification.recipient.extras = bitOperation(
                                    notification.recipient.extras,
                                    NotificationRecipientExtrases.READ,
                                    { positive: true, defaultExtras: NotificationRecipientExtrases.DEFAULT }
                                );
                                queryClient.setQueryData([QUERY_KEYS.GET_UNREAD_COUNT], (old: unknown) => {
                                    if (!old) return old;
                                    const typedOld = old as { count: number };
                                    const count = typedOld.count - 1;
                                    return { ...typedOld, count: count >= 0 ? count : 0 };
                                });
                            }
                        }
                    }
                    return typedOld;
                });
            }
        },
    });
};

export const useNotificationSubscribeMutation = () => {
    const axiosInstance = useGetNotificationAxiosInstance();

    return useMutation<true, Error, string>({
        mutationFn: async (token: string) => {
            const res = await subscribeToNotifications(axiosInstance, {
                platform: Platforms.WEB,
                token,
            });

            if (!res.success) throw handleNotificationError(res);
            return res.data;
        },
    });
};

export const useGetUnreadCount = () => {
    const axiosInstance = useGetNotificationAxiosInstance();

    return useQuery<INotificationUnreadCountResponse>({
        queryKey: [QUERY_KEYS.GET_UNREAD_COUNT],
        queryFn: async () => {
            const res = await getUnreadNotificationCount(axiosInstance);
            if (!res.success) throw handleNotificationError(res);
            return res.data;
        },
    });
};

export const useMarkAllNotificationsAsRead = () => {
    const axiosInstance = useGetNotificationAxiosInstance();

    return useMutation<true, Error>({
        mutationFn: async () => {
            const res = await readAllNotifications(axiosInstance);

            if (!res.success) throw handleNotificationError(res);
            enqueueSnackbar(res.message || "Bildirimler okundu olarak işaretlendi", {
                variant: "success",
            });

            return res.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.GET_NOTIFICATIONS],
            });
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.GET_UNREAD_COUNT],
            });
        },
    });
};
